import React, { useState } from 'react';
import { X, Loader2, Check, Circle } from 'lucide-react';
import { passwordChecks, passwordOk } from '@/utils/passwordRules';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function mapAuthError(status, data) {
  const detail = (data?.detail || '').toLowerCase();
  if (status === 409 || detail.includes('already exists'))
    return 'An account with that email already exists. Try signing in instead.';
  if (status === 429)
    return 'Too many attempts. Please wait a minute and try again.';
  if ((status === 400 || status === 422) && (detail.includes('password') || detail.includes('8 char')))
    return 'Your password needs 8+ characters with a capital letter, a lowercase letter, a number and a symbol.';
  if ((status === 400 || status === 422) && detail.includes('email'))
    return "That doesn't look like a valid email address.";
  if (status === 401)
    return 'Invalid email or password.';
  if (status >= 500)
    return 'Something went wrong on our end. Try again in a moment.';
  if (data?.detail)
    return data.detail;
  return 'Something went wrong. Try again.';
}

async function logAuthEvent(eventType, email, reason, userId) {
  const ts = new Date().toISOString();
  console.log(`[BA:AUTH:${eventType}]`, email, reason || '', ts);
  try {
    await fetch(`${BACKEND_URL}/api/v1/platform/auth-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: eventType, email, reason, user_id: userId }),
    });
  } catch (e) { /* non-blocking */ }
}

export const AuthModal = ({ isOpen, onClose, onSuccess, contextMessage }) => {
  const [tab, setTab] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSignIn = async () => {
    if (!email || !password) { setError('Email and password are required'); return; }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = mapAuthError(res.status, data);
        console.error('[BA:AUTH:ERROR]', res.status, data);
        setError(msg);
        logAuthEvent('LOGIN:FAIL', email, `${res.status}:${data?.detail || 'unknown'}`);
        return;
      }
      localStorage.setItem('ltbb-ba-token', data.jwt);
      const meRes = await fetch(`${BACKEND_URL}/api/v1/auth/me`, { headers: { Authorization: `Bearer ${data.jwt}` } });
      const user = meRes.ok ? await meRes.json() : { email, display_name: email.split('@')[0] };
      logAuthEvent('LOGIN:SUCCESS', email, null, user.id);
      onSuccess?.({ token: data.jwt, user });
      onClose?.();
    } catch (e) {
      console.error('[BA:AUTH:ERROR]', e);
      setError("Can't reach the server. Check your Wi-Fi and try again.");
      logAuthEvent('LOGIN:FAIL', email, 'network_error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!email || !password) { setError('Email and password are required'); return; }
    if (!passwordOk(password)) { setError('Your password is missing something. Check the list under the password box.'); return; }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, display_name: displayName || email.split('@')[0] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = mapAuthError(res.status, data);
        console.error('[BA:AUTH:ERROR]', res.status, data);
        setError(msg);
        logAuthEvent('SIGNUP:FAIL', email, `${res.status}:${data?.detail || 'unknown'}`);
        return;
      }
      localStorage.setItem('ltbb-ba-token', data.jwt);
      const user = { email, display_name: displayName || email.split('@')[0] };
      logAuthEvent('SIGNUP:SUCCESS', email, null);
      onSuccess?.({ token: data.jwt, user });
      onClose?.();
    } catch (e) {
      console.error('[BA:AUTH:ERROR]', e);
      setError("Can't reach the server. Check your Wi-Fi and try again.");
      logAuthEvent('SIGNUP:FAIL', email, 'network_error');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) { setError('Email is required'); return; }
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error('request_failed');
      setNotice('If an eligible account exists, a password reset link will be sent.');
    } catch (e) {
      setError("Can't reach the server. Check your Wi-Fi and try again.");
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (next) => { setTab(next); setError(null); setNotice(null); setPassword(''); };
  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (tab === 'signin') handleSignIn();
    else if (tab === 'create') handleCreateAccount();
    else handleForgotPassword();
  };

  return (
    <div data-testid="auth-modal" className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
      <div style={{ width: 400, background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2">
            <img src="/Pizzul_Hero.png" alt="" style={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid var(--green)', objectFit: 'cover' }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{tab === 'forgot' ? 'Reset your password' : contextMessage || 'Sign in to Beat Arranger'}</span>
          </div>
          <button data-testid="close-auth-modal" onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--bg-elevated)', color: 'var(--text-dim)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={14} />
          </button>
        </div>
        <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
          {[['signin', 'Sign In'], ['create', 'Create Account']].map(([id, label]) => (
            <button key={id} data-testid={`auth-tab-${id}`} onClick={() => switchTab(id)} className="flex-1 py-2.5" style={{ fontSize: 12, fontWeight: 600, background: 'transparent', border: 'none', cursor: 'pointer', color: tab === id ? 'var(--green)' : 'var(--text-dim)', borderBottom: tab === id ? '2px solid var(--green)' : '2px solid transparent' }}>{label}</button>
          ))}
        </div>
        <div className="px-5 py-5 flex flex-col gap-3" onKeyDown={handleKeyDown}>
          {tab === 'create' && (
            <input data-testid="auth-display-name" placeholder="Display name (what should we call you?)" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="outline-none" style={{ height: 40, borderRadius: 8, padding: '0 14px', background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 13 }} />
          )}
          <input data-testid="auth-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="outline-none" style={{ height: 40, borderRadius: 8, padding: '0 14px', background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 13 }} />
          {tab !== 'forgot' && <input data-testid="auth-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="outline-none" style={{ height: 40, borderRadius: 8, padding: '0 14px', background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 13 }} />}
          {tab === 'create' && (
            <ul data-testid="password-checklist" style={{ listStyle: 'none', margin: '-2px 0 0', padding: '0 2px', display: 'grid', gap: 3 }}>
              <li style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}>Your password needs:</li>
              {passwordChecks(password).map((rule) => (
                <li key={rule.id} data-testid={`password-rule-${rule.id}`} data-ok={rule.ok} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: rule.ok ? 'var(--green)' : 'var(--text-dim)' }}>
                  {rule.ok ? <Check size={13} /> : <Circle size={11} />}{rule.label}
                </li>
              ))}
            </ul>
          )}
          {error && <span data-testid="auth-error" style={{ fontSize: 12, color: 'var(--pink)', paddingLeft: 2 }}>{error}</span>}
          {notice && <span data-testid="auth-notice" style={{ fontSize: 12, color: 'var(--green)', paddingLeft: 2 }}>{notice}</span>}
          <button data-testid="auth-submit" onClick={tab === 'signin' ? handleSignIn : tab === 'create' ? handleCreateAccount : handleForgotPassword} disabled={loading} style={{ height: 42, borderRadius: 22, fontSize: 14, fontWeight: 600, background: loading ? 'var(--bg-elevated)' : 'var(--green)', color: loading ? 'var(--text-dim)' : 'var(--bg)', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {loading && <Loader2 size={15} className="animate-spin" />}
            {tab === 'signin' ? 'Sign In' : tab === 'create' ? 'Create Account' : 'Email reset link'}
          </button>
          {tab === 'signin' && <button data-testid="forgot-password" onClick={() => switchTab('forgot')} style={{ fontSize: 12, color: 'var(--text-dim)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center' }}>Forgot your password?</button>}
          <button onClick={() => switchTab(tab === 'signin' ? 'create' : 'signin')} style={{ fontSize: 12, color: 'var(--text-dim)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center' }}>
            {tab === 'signin' ? "Don't have an account? Create one" : tab === 'create' ? "Already have an account? Sign in" : 'Back to sign in'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
